/* eslint-disable es-x/no-promise-withresolvers -- this file is what provides it */

/* eslint-disable no-extend-native -- extending Array.prototype and String.prototype is the point */

// The shims that make the floor in .browserslistrc reachable. Nothing else in this project
// polyfills, and vite's target only transpiles SYNTAX, so every built-in below has to be handed
// to the browser or the code that calls it throws.
//
// Imported by app.js and by every page entry under frontend/views (a new entry has to import it
// too - there is no shared module that would do it for them), and prepended verbatim to pdfjs'
// prebuilt worker asset by the vite plugin in vite.config.ts, because a worker has its own global
// scope that the main thread's copy never reaches. That prepend is why nothing here declares a
// top-level binding: it would land in the minified worker's own top-level scope.
//
// Not to be confused with ./polyfills.js next door, which despite its name is a Deferred factory.

// Promise.withResolvers is ES2024: Chrome 119, Firefox 121, Safari 17.4 (March 2024). pdfjs-dist 5.4
// uses it 31 times in its main build and 12 times in the worker, so without this shim the signable
// letter view throws on load for every browser older than that - years above the floor.
// Our own code must not lean on it: the es-x guard in eslint.config.js keeps
// es-x/no-promise-withresolvers on for everything else.
if (typeof Promise.withResolvers !== 'function') {
    Promise.withResolvers = function withResolvers() {
        let resolve;
        let reject;
        const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });

        return { promise, resolve, reject };
    };
}

// The three ES2022/Web APIs that Safari 15.0-15.3 is missing and 15.4 has. They are what kept the
// floor at 15.4: @vueuse/core calls args.at(-1) inside useEventListener, which every composable in
// frontend/composables/device-context goes through, so the child check-in page threw during its
// first render and left a blank #page on iPads that stopped at iPadOS 15.3.

// One guard for both prototypes: Array.prototype.at and String.prototype.at shipped together.
if (typeof Array.prototype.at !== 'function') {
    const at = function at(index) {
        const length = this.length;
        const offset = Math.trunc(index) || 0;
        const position = offset < 0 ? length + offset : offset;

        return position < 0 || position >= length ? undefined : this[position];
    };

    Object.defineProperty(Array.prototype, 'at', { value: at, writable: true, configurable: true });
    Object.defineProperty(String.prototype, 'at', { value: at, writable: true, configurable: true });
}

if (typeof Object.hasOwn !== 'function') {
    Object.defineProperty(Object, 'hasOwn', {
        value: function hasOwn(target, key) {
            return Object.prototype.hasOwnProperty.call(target, key);
        },
        writable: true,
        configurable: true,
    });
}

if (typeof globalThis.structuredClone !== 'function') {
    // A subset of the structured clone algorithm: the types the callers actually pass.
    // @vuepic/vue-datepicker clones a calendar cell whose `value` is a Date, so JSON round-tripping
    // is not an option. Unclonable input (functions, DOM nodes) is copied by reference here where the
    // real algorithm throws a DataCloneError - a shim that is quieter than the API it stands in for,
    // not louder.
    const clone = function clone(value, seen) {
        if (value === null || typeof value !== 'object') {
            return value;
        }

        if (seen.has(value)) {
            return seen.get(value);
        }

        if (value instanceof Date) {
            return new Date(value.getTime());
        }

        if (value instanceof RegExp) {
            return new RegExp(value.source, value.flags);
        }

        if (Array.isArray(value)) {
            const copy = [];
            seen.set(value, copy);
            for (const item of value) {
                copy.push(clone(item, seen));
            }

            return copy;
        }

        if (value instanceof Map) {
            const copy = new Map();
            seen.set(value, copy);
            value.forEach((entry, key) => copy.set(clone(key, seen), clone(entry, seen)));

            return copy;
        }

        if (value instanceof Set) {
            const copy = new Set();
            seen.set(value, copy);
            value.forEach((entry) => copy.add(clone(entry, seen)));

            return copy;
        }

        const copy = {};
        seen.set(value, copy);
        for (const [key, entry] of Object.entries(value)) {
            copy[key] = clone(entry, seen);
        }

        return copy;
    };

    globalThis.structuredClone = function structuredClone(value) {
        return clone(value, new Map());
    };
}
